import clsx from 'clsx';
import { useMemo } from 'react';

import { TimeField } from '../TimeField';

import type { TimeFieldProps, TimeFieldVariant, TimeOption } from '../TimeField';
import type { ReactNode } from 'react';

import s from './TimeRangeField.module.css';

type TimeRangeFieldVariant = TimeFieldVariant;

export type { TimeOption };

export type TimeRangeValue = {
  from: string;
  to: string;
};

/** Filter end options to only show times after start time */
const filterEndOptions = (endOptions: TimeOption[] | undefined, startValue: string): TimeOption[] | undefined => {
  if (!endOptions || !startValue) {
    return endOptions;
  }

  return endOptions.filter((option) => {
    // Allow times after start time and also times past midnight (00:xx)
    if (option.value <= startValue && option.value === '00:00') {
      return true;
    }

    return option.value > startValue;
  });
};

type TimeRangeFieldProps = {
  /** Container className */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Element displayed between start and end fields (e.g., delete button) */
  divider?: ReactNode;
  /** End field props */
  endFieldProps?: Omit<TimeFieldProps, 'disabled' | 'onTimeChange' | 'options' | 'value' | 'variant'>;
  /** Label for end time field */
  endLabel?: string;
  /** Custom time options for end field */
  endOptions?: TimeOption[];
  /** End time value (HH:mm format) */
  endValue?: string;
  /** Make field take full width of its container */
  fullWidth?: boolean;
  /** Handler for any time change - returns both from and to values */
  onChange?: (value: TimeRangeValue) => void;
  /** Handler for end time change */
  onEndChange?: (value: string) => void;
  /** Handler for start time change */
  onStartChange?: (value: string) => void;
  /** Start field props */
  startFieldProps?: Omit<TimeFieldProps, 'disabled' | 'onTimeChange' | 'options' | 'value' | 'variant'>;
  /** Label for start time field */
  startLabel?: string;
  /** Custom time options for start field */
  startOptions?: TimeOption[];
  /** Start time value (HH:mm format) */
  startValue?: string;
  /** Visual state of the field
   * @default 'default'
   */
  variant?: TimeRangeFieldVariant;
};

export const TimeRangeField = ({
  className,
  disabled,
  divider,
  endFieldProps,
  endLabel,
  endOptions,
  endValue = '',
  fullWidth,
  onChange,
  onEndChange,
  onStartChange,
  startFieldProps,
  startLabel,
  startOptions,
  startValue = '',
  variant = 'default',
}: TimeRangeFieldProps) => {
  const handleStartChange = (value: string) => {
    onStartChange?.(value);

    // If end time is before or equal to new start time, reset it
    onChange?.({ from: value, to: endValue && endValue <= value ? '' : endValue });
  };

  const handleEndChange = (value: string) => {
    onEndChange?.(value);

    onChange?.({ from: startValue, to: value });
  };

  // Filter end options to only show times after start time
  const filteredEndOptions = useMemo(() => filterEndOptions(endOptions, startValue), [endOptions, startValue]);

  return (
    <div className={clsx(s.container, fullWidth && s['container--fullWidth'], className)}>
      <TimeField
        disabled={disabled}
        label={startLabel}
        onTimeChange={handleStartChange}
        options={startOptions}
        value={startValue}
        variant={variant}
        fullWidth
        {...startFieldProps}
        containerClassName={clsx(s.field, startFieldProps?.containerClassName)}
      />

      {Boolean(divider) && <div className={s.divider}>{divider}</div>}

      <TimeField
        disabled={disabled}
        label={endLabel}
        min={startValue || undefined}
        onTimeChange={handleEndChange}
        options={filteredEndOptions}
        value={endValue}
        variant={variant}
        fullWidth
        {...endFieldProps}
        containerClassName={clsx(s.field, endFieldProps?.containerClassName)}
      />
    </div>
  );
};
